/**
 * utils/timezones.ts
 *
 * Liste et validation des fuseaux horaires IANA utilisés par le paramètre
 * `default_timezone` (table `parameters`).
 *
 * Le fuseau est stocké sous forme de nom IANA (ex. « Africa/Casablanca ») :
 * c'est la seule forme qui suit correctement les changements d'heure, y compris
 * le passage du Maroc à UTC+0 pendant le Ramadan.
 */

/** Liste de repli si `Intl.supportedValuesOf` n'est pas disponible (vieux navigateurs). */
export const FALLBACK_TIMEZONES: string[] = [
  'Africa/Casablanca',
  'Africa/Algiers',
  'Africa/Tunis',
  'Africa/Cairo',
  'Europe/Paris',
  'Europe/London',
  'America/New_York',
  'Asia/Dubai',
  'Asia/Riyadh',
  'UTC',
];

/**
 * Décalages fixes proposés : **de -12:00 à +14:00, pas de 15 minutes** — la plage réellement
 * utilisée dans le monde (couvre les demis/quart d'heure : +05:30, +05:45, +09:30…).
 *
 * ⚠️ Utiles quand la base de fuseaux du navigateur/Node est en retard (ex. Maroc passé à
 * GMT permanent le 20/09/2026 : les tzdata < 2026b appliquent encore UTC+1 pour
 * `Africa/Casablanca`). `Intl` accepte « +00:00 » comme `timeZone` → affichage toujours juste.
 */
export const FIXED_OFFSET_OPTIONS: string[] = (() => {
  const list: string[] = [];
  for (let minutes = -12 * 60; minutes <= 14 * 60; minutes += 15) {
    const sign = minutes < 0 ? '-' : '+';
    const abs = Math.abs(minutes);
    const hh = String(Math.floor(abs / 60)).padStart(2, '0');
    const mm = String(abs % 60).padStart(2, '0');
    list.push(`${sign}${hh}:${mm}`);
  }
  return list;
})();

/** Fuseaux IANA connus du runtime (mémoïsé ; repli `FALLBACK_TIMEZONES`). */
let _ianaCache: string[] | null = null;
export function getIanaTimezones(): string[] {
  if (_ianaCache) return _ianaCache;
  try {
    const supported = (Intl as unknown as { supportedValuesOf?: (key: string) => string[] })
      .supportedValuesOf?.('timeZone');
    if (Array.isArray(supported) && supported.length > 0) {
      _ianaCache = supported;
      return _ianaCache;
    }
  } catch {
    /* environnement sans supportedValuesOf → liste de repli */
  }
  _ianaCache = FALLBACK_TIMEZONES;
  return _ianaCache;
}

/**
 * Liste plate des fuseaux proposés dans les sélecteurs (décalages fixes puis IANA).
 * La valeur courante est ajoutée en tête si elle n'est pas dans la liste
 * (garantit que le `<select>` contient toujours l'option sélectionnée).
 */
export function getTimezoneOptions(current?: string): string[] {
  const list = getIanaTimezones();
  const value = (current ?? '').trim();
  const withOffsets = [...FIXED_OFFSET_OPTIONS, ...list];
  if (value && !withOffsets.includes(value)) return [value, ...withOffsets];
  return withOffsets;
}

/** Vrai si `value` est un fuseau IANA reconnu par le runtime. */
export function isValidTimezone(value: string): boolean {
  if (!value || value.trim() === '') return false;
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: value });
    return true;
  } catch {
    return false;
  }
}

/**
 * Décalage courant d'un fuseau IANA **selon ce navigateur** (ex. « +00:00 »).
 *
 * ⚠️ À n'utiliser que comme **suggestion** : si la base de fuseaux du navigateur est
 * ancienne, la valeur renvoyée est fausse (ex. Africa/Casablanca → « +01:00 » avant
 * tzdata 2026b). Retourne `null` si indéterminable.
 */
export function currentOffsetOf(timeZone: string): string | null {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone,
      timeZoneName: 'longOffset',
    }).formatToParts(new Date());
    const name = parts.find(p => p.type === 'timeZoneName')?.value ?? '';
    const m = /GMT([+-]\d{2}:\d{2})?/.exec(name);
    if (!m) return null;
    return m[1] ?? '+00:00';
  } catch {
    return null;
  }
}
